import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { AdminInstallationType } from "../../../../../http/vertigrip/types/installationTypes";
import { useInstallationTypesApi } from "../../../../../http/vertigrip/useInstallationTypes";
import { useLowerElementsApi } from "../../../../../http/vertigrip/useLowerElements";
import { useVertigripSteelVersionsApi } from "../../../../../http/vertigrip/useSteelVersions";
import { ItemFixingWithQuantity } from "../../../../../types";
import Button from "../../../../ui/Button";
import ControlledNumericWithQuantity from "../../../../ui/ControlledNumericWithQuantity";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface LowerElementForm {
  item: SearchAdminItem | null;
  steelVersion: AdminCommonSteelVersion | null;
  installationType: AdminInstallationType | null;
  order: number | null;
  fixings: ItemFixingWithQuantity[];
  visibility: VisibilityScopeValue;
}

interface LowerElementFormProps {
  lowerElementId?: string;
  installationTypeId?: string;
  steelVersionId?: string;
  onCancel: () => void;
  onSave?: (itemId: string) => void;
}

const LowerElementForm: FC<LowerElementFormProps> = ({
  lowerElementId,
  installationTypeId,
  steelVersionId,
  onCancel,
  onSave,
}: LowerElementFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const installationTypesApi = useInstallationTypesApi();
  const steelVersionsApi = useVertigripSteelVersionsApi();
  const lowerElementsApi = useLowerElementsApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const [error, setError] = useState<CustomError>();

  const lowerElementSwr = lowerElementsApi.getAdminLowerElement(aborter.current.signal);

  const steelVersionsSwr = steelVersionsApi.useAdminSteelVersions({ limit: 100 }, aborter.current.signal);
  const installationTypesSwr = installationTypesApi.useAdminInstallationTypes({}, aborter.current.signal);

  const steelVersions: AdminCommonSteelVersion[] = (() => {
    const items = steelVersionsSwr.data?.items ?? [];

    if (!lowerElementSwr.data) {
      return items;
    }

    return !items.find((x) => x.id === lowerElementSwr.data?.vertigripSteelVersion.id)
      ? [...items, lowerElementSwr.data?.vertigripSteelVersion]
      : items;
  })();

  const installationTypes: AdminInstallationType[] = (() => {
    const items = installationTypesSwr.data?.items ?? [];

    if (!lowerElementSwr.data) {
      return items;
    }

    return !items.find((x) => x.id === lowerElementSwr.data?.installationType.id)
      ? [...items, lowerElementSwr.data?.installationType]
      : items;
  })();

  useEffect(() => {
    const anyError = lowerElementSwr.error || installationTypesSwr.error || steelVersionsSwr.error;

    if (anyError) {
      setError(anyError); // Set the error
      dispatch(showErrorNotification(i18n, anyError)); // Trigger the notification
    } else {
      setError(undefined); // Clear the error if no errors
    }
  }, [lowerElementSwr.error, installationTypesSwr.error, steelVersionsSwr.error]);

  return (
    <Form<LowerElementForm>
      defaultValues={{
        order: lowerElementSwr.data?.order ?? 50,
        fixings: [],
        visibility: lowerElementSwr.data?.visibility,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || !!error}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: onCancel,
          }}
          type="secondary"
        >
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);
        const body = {
          steelVersionId: formData.steelVersion?.id!,
          order: formData.order!,
          installationTypeId: formData?.installationType?.id!,
          fixings: formData.fixings
            .filter((x) => !!x.quantity)
            .map((x) => ({
              wallMaterialId: x.wallMaterial.id,
              quantity: x.quantity ?? 0,
            })),
          visibility: formData.visibility,
        };
        if (lowerElementSwr.data?.id) {
          try {
            await lowerElementsApi.updateAdminLowerElement(lowerElementSwr.data.id, body);
            onCancel();
            dispatch(showSuccessNotification(i18n.translation));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          try {
            const res = await lowerElementsApi.insertAdminLowerElement({
              ...body,
              itemId: formData.item?.id!,
            });
            onSave?.(res.id);
            dispatch(showSuccessNotification(i18n.translation));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, watch, disabled }) => {
        useEffect(() => {
          lowerElementId && lowerElementSwr.trigger(lowerElementId);
        }, [lowerElementId]);

        useEffect(() => {
          const steelVersion =
            steelVersions.find((x) => x.id === (lowerElementSwr.data?.vertigripSteelVersion.id ?? steelVersionId)) ??
            null;
          setValue("steelVersion", steelVersion);
        }, [steelVersions, lowerElementSwr.data?.vertigripSteelVersion.id]);

        useEffect(() => {
          const installationType =
            installationTypes.find(
              (x) => x.id === (lowerElementSwr.data?.installationType?.id ?? installationTypeId),
            ) ?? null;
          setValue("installationType", installationType);
        }, [installationTypes, lowerElementSwr.data?.vertigripSteelVersion.id]);

        useEffect(() => {
          if (lowerElementSwr.data) {
            setValue("order", lowerElementSwr.data.order);
            setValue("visibility", lowerElementSwr.data.visibility);
          }
        }, [lowerElementSwr.data]);

        useEffect(() => {
          if (lowerElementSwr.data) {
            const fixings: ItemFixingWithQuantity[] =
              installationTypesSwr.data?.items
                .find((x) => x.id === (lowerElementSwr.data?.installationType?.id ?? installationTypeId))
                ?.wallMaterials.reduce((rv: ItemFixingWithQuantity[], wm) => {
                  const f = lowerElementSwr.data?.fixings.find((x) => x.wallMaterialId === wm.id);
                  return [
                    ...rv,
                    {
                      wallMaterial: wm,
                      quantity: f?.quantity ?? 0,
                    },
                  ];
                }, []) ?? [];
            setValue("fixings", fixings);
          }
        }, [lowerElementSwr.data, installationTypesSwr.data]);

        const fixings = watch("fixings");

        return (
          <FormFieldsContainer fullWidth>
            {!!lowerElementSwr.data ? (
              <FormField label={i18n.translation.common.code}>
                <Input disabled value={lowerElementSwr.data?.code} />
              </FormField>
            ) : (
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                        {({ labelId }) => {
                          const searchTerm = useRef("");
                          const aborter = useRef(new AbortController());

                          const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                          // Fetch initial data on mount
                          useEffect(() => {
                            loadOptions(searchTerm.current).then(setDefaultOptions);
                          }, []);

                          return (
                            <CustomAsyncSelect
                              {...field}
                              inputId={labelId}
                              defaultOptions={defaultOptions}
                              loadOptions={loadOptions}
                              isClearable
                              isDisabled={disabled}
                              getOptionLabel={(x) => x.code}
                              getOptionValue={(x) => x.id}
                              onChange={(value) => setValue("item", value ?? null)}
                            />
                          );
                        }}
                      </FormField>
                    </>
                  );
                }}
              />
            )}
            <Controller
              name="steelVersion"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.steelVersions.singular}
                  error={fieldState.error}
                  hidden={!!steelVersionId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={steelVersions}
                      isLoading={steelVersionsSwr.isLoading}
                      isDisabled={!!steelVersionId || disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => setValue("steelVersion", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="installationType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.installationTypes.singular}
                  error={fieldState.error}
                  hidden={!!installationTypeId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={installationTypes}
                      isLoading={installationTypesSwr.isLoading}
                      isDisabled={!!installationTypeId || disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => setValue("installationType", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      readOnly={disabled}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />

            {fixings.length > 0 && (
              <FormField label={i18n.translation.common.nrOfFixingItemsRequired}>
                <ControlledNumericWithQuantity.List>
                  {fixings.map((x) => (
                    <Fragment key={x.wallMaterial.id}>
                      <ControlledNumericWithQuantity
                        item={{
                          id: x.wallMaterial.id,
                          display: x.wallMaterial.name,
                          disabled: true,
                        }}
                        controls={{
                          scale: 1,
                        }}
                        quantity={{
                          quantity: x.quantity ?? 0,
                          onUpdate: (id, quantity) =>
                            setValue(
                              "fixings",
                              fixings.reduce((rv: ItemFixingWithQuantity[], i) => {
                                if (i.wallMaterial.id === id) {
                                  return [...rv, { ...i, quantity: quantity }];
                                }
                                return [...rv, i];
                              }, []),
                            ),
                        }}
                      />
                    </Fragment>
                  ))}
                </ControlledNumericWithQuantity.List>
              </FormField>
            )}
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default LowerElementForm;
